import { useEffect, useSyncExternalStore } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AuthResponse, AuthUser, MeResponse } from '@nargus/shared';
import { authResource } from './auth.resource';
import { getToken, setToken, subscribeToken } from '../token';

export const meQuery = () =>
  queryOptions({
    queryKey: ['auth', 'me'],
    queryFn: authResource.me,
    staleTime: 5 * 60_000,
  });

function useToken(): string | null {
  return useSyncExternalStore(subscribeToken, getToken, () => null);
}

export function useSession(): { user: AuthUser | null; isLoading: boolean } {
  const token = useToken();
  const query = useQuery({ ...meQuery(), enabled: Boolean(token) });

  const rejected = query.isError;
  useEffect(() => {
    if (rejected) setToken(null);
  }, [rejected]);

  if (!token) return { user: null, isLoading: false };
  return { user: query.data?.user ?? null, isLoading: query.isPending };
}

function adopt(client: QueryClient, res: AuthResponse): void {
  setToken(res.token);
  client.setQueryData(meQuery().queryKey, { user: res.user });
}

export function useLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: authResource.login,
    onSuccess: (res) => adopt(client, res),
  });
}

export function useRegister() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: authResource.register,
    onSuccess: (res) => adopt(client, res),
  });
}

function useAvatarMutation<TInput>(mutationFn: (input: TInput) => Promise<MeResponse>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (res) => {
      client.setQueryData(meQuery().queryKey, res);
      void client.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export function useSetAvatar() {
  return useAvatarMutation((file: File) => authResource.setAvatar(file));
}

export function useRemoveAvatar() {
  return useAvatarMutation(() => authResource.removeAvatar());
}

export function useLogout(): () => void {
  const client = useQueryClient();
  return () => {
    setToken(null);
    client.clear();
  };
}
